import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { 
  FaBox, FaPlus, FaSave, FaBroom, FaEye, FaEdit, FaTrash,
  FaWarehouse, FaTruck, FaCalculator, FaFileInvoice,
  FaCube, FaDollarSign, FaPercent
} from "react-icons/fa";
import Select from 'react-select';
import fetchData from "../../functions/fetchData";
import { getHeaders } from "../../utility/getHeader";
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import "react-toastify/dist/ReactToastify.css";

export default function NewStock() {
  const navigate = useNavigate();
  
  // Refs for focusing
  const itemSelectRefs = React.useRef([]);
  
  // Stock entry state
  const [stockEntries, setStockEntries] = useState([{
    id: Date.now(),
    item_id: '',
    itemName: '',
    stock_in: '',
    unit: '',
    purchase_price: '',
    vat: '',
    subtotal: '',
    vatAmount: '',
    netAmount: '',
    refno: '',
    pdate: new Date().toISOString().split('T')[0]
  }]);
  
  const [currentEntry, setCurrentEntry] = useState(0);
  const [supplier, setSupplier] = useState(null);
  
  // Additional form fields
  const [billRefNo, setBillRefNo] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [remarks, setRemarks] = useState('');
  const [billTotal, setBillTotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [savedToStorage, setSavedToStorage] = useState(false);
  
  // Data states
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stockHistory, setStockHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoVat, setAutoVat] = useState(true);
  const [vatPercentage] = useState(7);
  
  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editRefNo, setEditRefNo] = useState('');
  const [searchRefNo, setSearchRefNo] = useState('');
  const [refNoSuggestions, setRefNoSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);


  // Note: This page intentionally skips token validation and interceptor-based auto-logout

  // Load initial data
  useEffect(() => {
    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load from local storage on component mount
  useEffect(() => {
    loadFromLocalStorage();
  }, []);

  // Update bill total whenever stock entries change
  useEffect(() => {
    const total = stockEntries.reduce((sum, entry) => {
      const netAmount = parseFloat(entry.netAmount) || 0;
      return sum + netAmount;
    }, 0);
    setBillTotal(total);
  }, [stockEntries]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleShortcut = (e) => {
      // F1 - Add New Entry
      if (e.key === 'F1') {
        e.preventDefault();
        addNewStockEntry();
      }
      // F2 - Save Bill to Database
      else if (e.key === 'F2') {
        e.preventDefault();
        saveBillToDatabase();
      }
      // F3 - Clear Form
      else if (e.key === 'F3') {
        e.preventDefault();
        clearForm();
      }
      // F4 - Refresh Data
      else if (e.key === 'F4') {
        e.preventDefault();
        loadStockHistory();
      }
      // Ctrl+Enter - Add New Row
      else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        addNewStockEntry();
      }
      // Ctrl+Delete - Remove Last Row
      else if (e.ctrlKey && (e.key === 'Delete' || e.key === 'Del')) {
        e.preventDefault();
        removeLastEntry();
      }
    };
    
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    console.log('🔄 Starting loadInitialData...');
    try {
      // Load items from items table and suppliers
      console.log('📦 Fetching items and suppliers...');
      const [itemsData, suppliersData] = await Promise.all([
        fetchData('items', null, 'id', {'isstockable': '1'}),
        fetchData('suppliers', null, 'id', {})
      ]);
      
      console.log('✅ Raw items received:', itemsData?.length || 0, itemsData);
      console.log('✅ Raw suppliers received:', suppliersData?.length || 0, suppliersData);
      
      // Filter items for stockable ones only
      const stockableItems = (itemsData || []).filter(item => 
        item.isstockable === '1' || item.isstockable === 1 || item.isstockable === true
      );
      
      console.log('📊 Stockable filter results:');
      console.log('  - Total items loaded:', itemsData?.length || 0);
      console.log('  - Stockable items filtered:', stockableItems.length);
      console.log('  - Sample stockable items:', stockableItems.slice(0, 3));
      
      // Set state
      setItems(stockableItems);
      setSuppliers(suppliersData || []);
      console.log('✅ State updated - Items:', stockableItems.length, 'Suppliers:', suppliersData?.length || 0);
      
      // Test itemOptions generation
      const testOptions = stockableItems.map(item => ({
        value: item.id,
        label: `${item.item_code} - ${item.iname}`,
        unit: item.unit,
        purchase_price: item.offerprice || item.mrp || 0
      }));
      console.log('🎯 Generated item options:', testOptions.length, testOptions.slice(0, 3));
      
      if (stockableItems.length === 0) {
        console.warn('⚠️ WARNING: No stockable items found!');
        console.warn('   Check if items have isstockable = 1 in database');
        toast.warning('No stockable items found. Please check item settings.');
      }
      
      // Load recent stock history
      await loadStockHistory();
      console.log('✅ loadInitialData completed successfully');
    } catch (error) {
      console.error('❌ Error loading initial data:', error);
      toast.error('Failed to load initial data: ' + error.message);
    }
    setLoading(false);
  }, []);

  const loadStockHistory = async () => {
    try {
      const response = await axios.get(
        '/inventory/details', getHeaders() 
      );
      
      if (response.data.success) {
        const history = response.data.data || [];
        setStockHistory(history);
        
        // Extract unique reference numbers for autocomplete
        const uniqueRefNos = [...new Set(history.map(item => item.refno).filter(Boolean))];
        setRefNoSuggestions(uniqueRefNos.sort((a, b) => b.localeCompare(a))); // Sort descending
      }
    } catch (error) {
      console.error('Error loading stock history:', error);
    }
  };

  // Local Storage Functions
  const saveToLocalStorage = () => {
    const billData = {
      stockEntries: stockEntries.filter(entry => entry.item_id && entry.stock_in),
      supplier: supplier,
      billRefNo: billRefNo,
      invoiceDate: invoiceDate,
      remarks: remarks,
      discount: discount,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem('pendingStockBill', JSON.stringify(billData));
    setSavedToStorage(true);
    toast.success('Bill saved to local storage successfully!');
  };

  const loadFromLocalStorage = () => {
    const savedBill = localStorage.getItem('pendingStockBill');
    if (savedBill) {
      const billData = JSON.parse(savedBill);
      setStockEntries(billData.stockEntries || []);
      setSupplier(billData.supplier || null);
      setBillRefNo(billData.billRefNo || '');
      setInvoiceDate(billData.invoiceDate || new Date().toISOString().split('T')[0]);
      setRemarks(billData.remarks || '');
      setDiscount(billData.discount || 0);
      setSavedToStorage(true);
      toast.info('Loaded bill from local storage');
    }
  };

  const clearLocalStorage = () => {
    localStorage.removeItem('pendingStockBill');
    setSavedToStorage(false);
  };

  const addNewStockEntry = () => {
    const newEntry = {
      id: Date.now(),
      item_id: '',
      itemName: '',
      stock_in: '',
      unit: '',
      purchase_price: '',
      vat: '',
      subtotal: '',
      vatAmount: '',
      netAmount: '',
      refno: '',
      pdate: new Date().toISOString().split('T')[0]
    };
    
    setStockEntries(prev => [...prev, newEntry]);
    setCurrentEntry(stockEntries.length);
    
    // Auto-focus on the new row's item select after a brief delay
    setTimeout(() => {
      const newIndex = stockEntries.length;
      if (itemSelectRefs.current[newIndex]) {
        itemSelectRefs.current[newIndex].focus();
      }
    }, 100);
  };

  const removeLastEntry = () => {
    if (stockEntries.length > 1) {
      setStockEntries(prev => prev.slice(0, -1));
      setCurrentEntry(Math.max(0, currentEntry - 1));
    }
  };

  const clearForm = () => {
    setStockEntries([{
      id: Date.now(),
      item_id: '',
      itemName: '',
      stock_in: '',
      unit: '',
      purchase_price: '',
      vat: '',
      subtotal: '',
      vatAmount: '',
      netAmount: '',
      refno: '',
      pdate: new Date().toISOString().split('T')[0]
    }]);
    setCurrentEntry(0);
    setSupplier(null);
    setBillRefNo('');
    setInvoiceDate(new Date().toISOString().split('T')[0]);
    setRemarks('');
    setDiscount(0);
    clearLocalStorage();
    toast.info('Form cleared');
  };

  const handleItemSelect = (selectedOption, entryIndex) => {
    if (!selectedOption) return;
    
    const item = items.find(i => i.id === selectedOption.value);
    if (!item) return;
    
    setStockEntries(prev => prev.map((entry, index) => 
      index === entryIndex ? {
        ...entry,
        item_id: item.id,
        itemName: selectedOption.label,
        unit: item.unit || '',
        purchase_price: item.offerprice || item.mrp || '',
      } : entry
    ));
    
    // Auto-calculate if purchase price exists
    const price = item.offerprice || item.mrp;
    if (price) {
      calculateAmounts(entryIndex, 'purchase_price', price);
    }
  };

  const handleInputChange = (entryIndex, field, value) => {
    setStockEntries(prev => prev.map((entry, index) => 
      index === entryIndex ? { ...entry, [field]: value } : entry
    ));
    
    // Auto-calculate amounts when relevant fields change
    if (['purchase_price', 'stock_in', 'vat'].includes(field)) {
      calculateAmounts(entryIndex, field, value);
    }
  };

  const calculateAmounts = (entryIndex, changedField, value) => {
    setStockEntries(prev => prev.map((entry, index) => {
      if (index !== entryIndex) return entry;
      
      const updatedEntry = { ...entry, [changedField]: value };
      const stockIn = parseFloat(updatedEntry.stock_in) || 0;
      const purchasePrice = parseFloat(updatedEntry.purchase_price) || 0;
      const vatRate = autoVat ? vatPercentage : (parseFloat(updatedEntry.vat) || 0);
      
      if (stockIn > 0 && purchasePrice > 0) {
        const subtotal = stockIn * purchasePrice;
        const vatAmount = (subtotal * vatRate) / 100;
        const netAmount = subtotal + vatAmount;
        
        return {
          ...updatedEntry,
          subtotal: subtotal.toFixed(2),
          vat: vatRate.toString(),
          vatAmount: vatAmount.toFixed(2),
          netAmount: netAmount.toFixed(2)
        };
      }
      
      return updatedEntry;
    }));
  };

  // Save Bill to Database
  const saveBillToDatabase = async () => {
    console.log('🔵 Save Bill triggered');
    console.log('Supplier:', supplier);
    console.log('Stock Entries:', stockEntries);
    console.log('Bill Ref No:', billRefNo);
    
    if (!supplier) {
      toast.error('Please select a supplier');
      return;
    }
    
    const validEntries = stockEntries.filter(entry => entry.item_id && entry.stock_in);
    console.log('Valid Entries:', validEntries);
    
    if (validEntries.length === 0) {
      toast.error('Please add at least one valid item');
      return;
    }
    
    if (!billRefNo.trim()) {
      toast.error('Please enter Reference Number');
      return;
    }
    
    // Don't disable button during save
    // setLoading(true);
    try {
      const billData = {
        supplier_id: supplier.value,
        description: remarks || `Purchase from ${supplier.label}`,
        items: validEntries.map(entry => ({
          item_id: entry.item_id,
          stock_in: parseFloat(entry.stock_in),
          unit: entry.unit || '',
          refno: billRefNo,
          pdate: invoiceDate,
          purchase_price: parseFloat(entry.purchase_price),
          vat: parseFloat(entry.vat) || 0,
          subtotal: parseFloat(entry.subtotal) || 0,
          vatAmount: parseFloat(entry.vatAmount) || 0,
          netAmount: parseFloat(entry.netAmount)
        }))
      };
      
      console.log('📤 Sending bill data:', billData);
      console.log('📤 API URL:', '/inventory/bulk-insert');
      
      const response = await axios.post(
        '/inventory/bulk-insert',
        billData,
        getHeaders()
      );
      
      console.log('✅ Response:', response.data);
      
      if (response.data.success) {
        toast.success('Bill saved successfully!');
        clearLocalStorage();
        clearForm();
        setBillRefNo('');
        setInvoiceDate(new Date().toISOString().split('T')[0]);
        setRemarks('');
        setDiscount(0);
        setSupplier(null);
        await loadStockHistory();
      } else {
        throw new Error(response.data.message || 'Failed to save bill');
      }
    } catch (error) {
      console.error('❌ Error saving bill:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error message:', error.message);
      
      // Check for 401 authentication error
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
        // Optionally redirect to login
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection and try again.');
      } else if (error.response) {
        // Server responded with error
        toast.error(error.response?.data?.message || `Error: ${error.response.status} - ${error.response.statusText}`);
      } else if (error.request) {
        // Request was made but no response received
        toast.error('No response from server. Please check if backend is running.');
      } else {
        // Something else happened
        toast.error(error.message || 'Failed to save bill to database');
      }
    } finally {
      // Don't disable button during save
      // setLoading(false);
    }
  };

  // Handle reference number input change with filtering
  const handleRefNoChange = (value) => {
    setSearchRefNo(value);
    
    if (value.trim()) {
      const filtered = refNoSuggestions.filter(refNo => 
        refNo.toLowerCase().includes(value.toLowerCase())
      );
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  // Select suggestion
  const selectRefNoSuggestion = (refNo) => {
    setSearchRefNo(refNo);
    setShowSuggestions(false);
  };

  // Load inventory for editing by reference number
  const loadInventoryForEdit = async () => {
    if (!searchRefNo.trim()) {
      toast.error('Please enter Reference Number');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `/inventory/by-refno/${searchRefNo.trim()}`,
        getHeaders()
      );

      if (response.data.success) {
        const { items: inventoryItems, supplier_id, supplier_name, reference_number, purchase_date } = response.data.data;

        // Set supplier
        const supplierOption = suppliers.find(s => s.id === supplier_id);
        if (supplierOption) {
          setSupplier({
            value: supplier_id,
            label: supplierOption.supplier_name || supplierOption.company_name || `Supplier ${supplier_id}`
          });
        }

        // Set bill details
        setBillRefNo(reference_number);
        setInvoiceDate(purchase_date ? new Date(purchase_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);

        // Load items into stock entries
        const loadedEntries = inventoryItems.map((item, index) => ({
          id: Date.now() + index,
          item_id: item.item_id,
          itemName: `${item.item_code} - ${item.item_name}`,
          stock_in: item.stock_in,
          unit: item.unit,
          purchase_price: item.purchase_price,
          vat: item.vat || 7,
          subtotal: item.subtotal,
          vatAmount: item.vatAmount,
          netAmount: item.netAmount,
          refno: reference_number,
          pdate: purchase_date ? new Date(purchase_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
        }));

        setStockEntries(loadedEntries);
        setIsEditMode(true);
        setEditRefNo(reference_number);
        toast.success(`Loaded ${inventoryItems.length} items for editing from Ref: ${reference_number}`);
      }
    } catch (error) {
      console.error('Error loading inventory:', error);
      toast.error(error.response?.data?.message || 'Failed to load inventory for editing');
    }
    setLoading(false);
  };

  // Save edited inventory
  const saveEditedInventory = async () => {
    if (!supplier) {
      toast.error('Please select a supplier');
      return;
    }

    const validEntries = stockEntries.filter(entry => entry.item_id && entry.stock_in);
    if (validEntries.length === 0) {
      toast.error('Please add at least one valid item');
      return;
    }

    if (!editRefNo.trim()) {
      toast.error('Reference number is missing');
      return;
    }

    // For single item edit - send item_id and stock_in directly
    if (validEntries.length === 1) {
      setLoading(true);
      try {
        const entry = validEntries[0];
        const editData = {
          supplier_id: supplier.value,
          item_id: entry.item_id,
          stock_in: parseFloat(entry.stock_in),
          unit: entry.unit || '',
          pdate: invoiceDate,
          purchase_price: parseFloat(entry.purchase_price),
          vat: parseFloat(entry.vat) || 0,
          subtotal: parseFloat(entry.subtotal) || 0,
          vatAmount: parseFloat(entry.vatAmount) || 0,
          netAmount: parseFloat(entry.netAmount)
        };

        const response = await axios.put(
          `/inventory/edit-by-refno/${editRefNo}`,
          editData,
          getHeaders()
        );

        if (response.data.success) {
          toast.success('Inventory updated successfully!');
          cancelEditMode();
          await loadStockHistory();
        } else {
          throw new Error(response.data.message || 'Failed to update inventory');
        }
      } catch (error) {
        console.error('Error updating inventory:', error);
        if (error.response?.status === 401) {
          toast.error('Authentication failed. Please login again.');
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else {
          toast.error(error.response?.data?.message || 'Failed to update inventory');
        }
      }
      setLoading(false);
    } else {
      // For multiple items - use bulk edit endpoint
      setLoading(true);
      try {
        const bulkData = {
          supplier_id: supplier.value,
          reference_number: editRefNo,
          items: validEntries.map(entry => ({
            item_id: entry.item_id,
            stock_in: parseFloat(entry.stock_in),
            unit: entry.unit || '',
            pdate: invoiceDate,
            purchase_price: parseFloat(entry.purchase_price),
            vat: parseFloat(entry.vat) || 0,
            subtotal: parseFloat(entry.subtotal) || 0,
            vatAmount: parseFloat(entry.vatAmount) || 0,
            netAmount: parseFloat(entry.netAmount)
          }))
        };

        const response = await axios.put(
          `/inventory/bulk-edit-by-refno/${editRefNo}`,
          bulkData,
          getHeaders()
        );

        if (response.data.success) {
          toast.success(`Inventory updated successfully! ${validEntries.length} items saved.`);
          cancelEditMode();
          await loadStockHistory();
        } else {
          throw new Error(response.data.message || 'Failed to update inventory');
        }
      } catch (error) {
        console.error('Error updating inventory (bulk):', error);
        if (error.response?.status === 401) {
          toast.error('Authentication failed. Please login again.');
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else {
          toast.error(error.response?.data?.message || 'Failed to update inventory. Try editing items one at a time.');
        }
      }
      setLoading(false);
    }
  };

  // Cancel edit mode
  const cancelEditMode = () => {
    if (window.confirm('Cancel editing? All changes will be lost.')) {
      setIsEditMode(false);
      setEditRefNo('');
      setSearchRefNo('');
      clearForm();
      toast.info('Edit cancelled');
    }
  };

  const handleEditStock = (stock) => {
    console.log('Editing stock:', stock);
    // Implementation for editing stock entry
  };

  const handleDeleteStock = async (stockId) => {
    if (!window.confirm('Are you sure you want to delete this stock entry?')) return;
    
    try {
      const response = await axios.delete(
        `/inventory/${stockId}`,
        { 
          ...getHeaders(),
          data: { reason: 'User requested deletion' }
        }
      );
      
      if (response.data.success) {
        toast.success('Stock entry deleted successfully');
        loadStockHistory();
      } else {
        toast.error('Failed to delete stock entry');
      }
    } catch (error) {
      console.error('Error deleting stock entry:', error);
      toast.error('Failed to delete stock entry');
    }
  };

  // Format options for react-select
  const itemOptions = items.map(item => ({
    value: item.id,
    label: `${item.item_code} - ${item.iname}`,
    unit: item.unit,
    purchase_price: item.offerprice || item.mrp || 0
  }));

  const supplierOptions = suppliers.map(supplier => ({
    value: supplier.id,
    label: supplier.supplier_name || `Supplier ${supplier.id}`
  }));

  // Calculate totals for all entries
  const calculateTotals = () => {
    const validEntries = stockEntries.filter(entry => parseFloat(entry.netAmount) > 0);
    const totalAmount = validEntries.reduce((sum, entry) => sum + parseFloat(entry.netAmount || 0), 0);
    const totalVAT = validEntries.reduce((sum, entry) => sum + parseFloat(entry.vatAmount || 0), 0);
    const totalSubtotal = validEntries.reduce((sum, entry) => sum + parseFloat(entry.subtotal || 0), 0);
    
    return { totalAmount, totalVAT, totalSubtotal, itemCount: validEntries.length };
  };

  const totals = calculateTotals();

  const renderContent = () => (
    <div style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-lg" style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            borderRadius: '12px',
            minHeight: '120px'
          }}>
            <div className="card-body p-3 text-white position-relative">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title mb-1 opacity-90" style={{ fontSize: '0.9rem' }}>Total Items</h6>
                  <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>{totals.itemCount}</h4>
                </div>
                <div className="bg-white bg-opacity-25 rounded-circle p-2">
                  <FaCube size={20} className="text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-md-3">
          <div className="card border-0 shadow-lg" style={{ 
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
            borderRadius: '12px',
            minHeight: '120px'
          }}>
            <div className="card-body p-3 text-white position-relative">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title mb-1 opacity-90" style={{ fontSize: '0.9rem' }}>Subtotal</h6>
                  <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>฿{totals.totalSubtotal.toFixed(2)}</h4>
                </div>
                <div className="bg-white bg-opacity-25 rounded-circle p-2">
                  <FaCalculator size={20} className="text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-md-3">
          <div className="card border-0 shadow-lg" style={{ 
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
            borderRadius: '12px',
            minHeight: '120px'
          }}>
            <div className="card-body p-3 text-white position-relative">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title mb-1 opacity-90" style={{ fontSize: '0.9rem' }}>Total VAT</h6>
                  <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>฿{totals.totalVAT.toFixed(2)}</h4>
                </div>
                <div className="bg-white bg-opacity-25 rounded-circle p-2">
                  <FaPercent size={20} className="text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-md-3">
          <div className="card border-0 shadow-lg" style={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', 
            borderRadius: '12px',
            minHeight: '120px'
          }}>
            <div className="card-body p-3 text-white position-relative">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-title mb-1 opacity-90" style={{ fontSize: '0.9rem' }}>Net Amount</h6>
                  <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>฿{totals.totalAmount.toFixed(2)}</h4>
                </div>
                <div className="bg-white bg-opacity-25 rounded-circle p-2">
                  <FaDollarSign size={20} className="text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Supplier and Bill Information - Combined */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-lg" style={{ 
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
            overflow: 'hidden'
          }}>
            <div className="card-header border-0 pb-2" style={{ 
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(10px)'
            }}>
              <h6 className="mb-0 text-white d-flex align-items-center fw-bold">
                <FaTruck className="me-2" size={20} />
                Supplier & Bill Information
              </h6>
            </div>
            <div className="card-body pt-3" style={{ background: 'white' }}>
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label small fw-semibold text-dark">Select Supplier *</label>
                  {/* Debug info */}
                  <div className="small mb-2" style={{ 
                    padding: '6px 10px',
                    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                    borderRadius: '6px',
                    color: '#495057'
                  }}>
                    <strong>📊 Status:</strong> {suppliers.length} supplier(s) loaded, {supplierOptions.length} option(s) available
                    {suppliers.length === 0 && <span className="text-danger ms-2">⚠️ No suppliers found!</span>}
                  </div>
                  <Select
                    value={supplier}
                    onChange={setSupplier}
                    options={supplierOptions}
                    placeholder="🔍 Choose supplier..."
                    isSearchable
                    className="react-select-container"
                    classNamePrefix="react-select"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderRadius: '10px',
                        borderColor: '#2193b0',
                        borderWidth: '2px',
                        minHeight: '44px',
                        boxShadow: 'none',
                        '&:hover': {
                          borderColor: '#1a7a8e'
                        }
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#2193b0' : state.isFocused ? '#e3f2fd' : 'white',
                        color: state.isSelected ? 'white' : '#333',
                        cursor: 'pointer'
                      })
                    }}
                  />
                </div>
                <div className="col-md-6 d-flex align-items-end">
                  {supplier ? (
                    <div className="w-100 p-3" style={{ 
                      background: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 100%)',
                      borderRadius: '12px',
                      boxShadow: '0 4px 15px rgba(33, 147, 176, 0.3)'
                    }}>
                      <div className="text-white">
                        <div className="small fw-semibold mb-1">✅ Selected Supplier</div>
                        <div className="fs-6 fw-bold">{supplier.label}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="w-100 p-3 text-center" style={{ 
                      background: 'linear-gradient(135deg, #f5f7fa 0%, #e3f2fd 100%)',
                      borderRadius: '12px',
                      border: '2px dashed #90caf9'
                    }}>
                      <div className="text-muted small">
                        <FaTruck size={24} className="mb-2" style={{ opacity: 0.5 }} />
                        <div>No supplier selected</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Bill Information Section */}
              <div className="row">
                <div className="col-md-3">
                  <label className="form-label small fw-medium">Reference No. *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={billRefNo}
                    onChange={(e) => setBillRefNo(e.target.value)}
                    placeholder="Enter ref number..."
                    style={{ borderRadius: '8px', borderColor: '#e2e8f0' }}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label small fw-medium">Invoice Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    style={{ borderRadius: '8px', borderColor: '#e2e8f0' }}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label small fw-medium">Discount</label>
                  <input
                    type="number"
                    className="form-control"
                    value={discount}
                    onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                    placeholder="0.00"
                    step="0.01"
                    style={{ borderRadius: '8px', borderColor: '#e2e8f0' }}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label small fw-medium">Total Amount</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={`฿${(billTotal - discount).toFixed(2)}`}
                    readOnly
                    style={{ borderRadius: '8px', borderColor: '#e2e8f0' }}
                  />
                </div>
              </div>
              <div className="row mt-3">
                <div className="col-md-12">
                  <label className="form-label small fw-medium">Remarks</label>
                  <textarea
                    className="form-control"
                    rows="2"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Enter any remarks or notes..."
                    style={{ borderRadius: '8px', borderColor: '#e2e8f0' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stock Entry Form */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <div className="card-header bg-transparent border-0 pb-0">
              <div className="d-flex justify-content-between align-items-center">
                <h6 className="mb-0 text-muted d-flex align-items-center">
                  <FaBox className="me-2" />
                  Stock Entries
                </h6>
                <small className="text-muted">
                  Press Ctrl+Enter to add row, Ctrl+Del to remove last row
                </small>
              </div>
            </div>
            <div className="card-body pt-2">
              <div className="table-responsive">
                <table className="table table-borderless">
                  <thead>
                    <tr style={{ backgroundColor: '#f8fafc' }}>
                      <th className="px-3 py-2 border-0">Item</th>
                      <th className="px-3 py-2 border-0" style={{ width: '100px' }}>Stock In</th>
                      <th className="px-3 py-2 border-0" style={{ width: '80px' }}>Unit</th>
                      <th className="px-3 py-2 border-0" style={{ width: '100px' }}>Price</th>
                      <th className="px-3 py-2 border-0" style={{ width: '80px' }}>VAT%</th>
                      <th className="px-3 py-2 border-0" style={{ width: '100px' }}>Net Amount</th>
                      <th className="px-3 py-2 border-0" style={{ width: '120px' }}>Ref No</th>
                      <th className="px-3 py-2 border-0" style={{ width: '120px' }}>Date</th>
                      <th className="px-3 py-2 border-0" style={{ width: '50px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockEntries.map((entry, index) => (
                      <tr key={entry.id} className="border-bottom">
                        <td className="px-3 py-2">
                          <Select
                            ref={el => (itemSelectRefs.current[index] = el)}
                            value={entry.item_id ? { value: entry.item_id, label: entry.itemName } : null}
                            onChange={(option) => {
                              console.log('🎯 Item selected:', option);
                              handleItemSelect(option, index);
                            }}
                            options={itemOptions}
                            placeholder={itemOptions.length > 0 ? `Select from ${itemOptions.length} items...` : "No items available"}
                            isSearchable
                            isClearable
                            openMenuOnClick={true}
                            menuPlacement="auto"
                            menuPortalTarget={document.body}
                            noOptionsMessage={() => "No items found - Check isstockable=1"}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                borderRadius: '6px',
                                borderColor: itemOptions.length === 0 ? '#dc3545' : '#e2e8f0',
                                minHeight: '35px'
                              }),
                              menuPortal: (base) => ({ ...base, zIndex: 9999 })
                            }}
                            filterOption={(option, inputValue) =>
                              option.label.toLowerCase().includes(inputValue.toLowerCase())
                            }
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={entry.stock_in}
                            onChange={(e) => handleInputChange(index, 'stock_in', e.target.value)}
                            placeholder="0"
                            style={{ borderRadius: '6px' }}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={entry.unit}
                            onChange={(e) => handleInputChange(index, 'unit', e.target.value)}
                            placeholder="Unit"
                            style={{ borderRadius: '6px' }}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={entry.purchase_price}
                            onChange={(e) => handleInputChange(index, 'purchase_price', e.target.value)}
                            placeholder="0.00"
                            step="0.01"
                            style={{ borderRadius: '6px' }}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            className="form-control form-control-sm"
                            value={entry.vat}
                            onChange={(e) => handleInputChange(index, 'vat', e.target.value)}
                            placeholder="7"
                            disabled={autoVat}
                            style={{ borderRadius: '6px' }}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="fw-medium text-success">
                            ฿{parseFloat(entry.netAmount || 0).toFixed(2)}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            value={entry.refno}
                            onChange={(e) => handleInputChange(index, 'refno', e.target.value)}
                            placeholder="Ref#"
                            style={{ borderRadius: '6px' }}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={entry.pdate}
                            onChange={(e) => handleInputChange(index, 'pdate', e.target.value)}
                            style={{ borderRadius: '6px' }}
                          />
                        </td>
                        <td className="px-3 py-2">
                          {stockEntries.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => setStockEntries(prev => prev.filter((_, i) => i !== index))}
                              style={{ borderRadius: '6px' }}
                            >
                              <FaTrash size={12} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stock History */}
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '12px' }}>
            <div className="card-header bg-transparent border-0 pb-0">
              <h6 className="mb-0 text-muted d-flex align-items-center">
                <FaWarehouse className="me-2" />
                Recent Stock Entries
              </h6>
            </div>
            <div className="card-body pt-2">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="px-3 py-2 border-0">Item</th>
                      <th className="px-3 py-2 border-0">Stock In</th>
                      <th className="px-3 py-2 border-0">Purchase Price</th>
                      <th className="px-3 py-2 border-0">Net Amount</th>
                      <th className="px-3 py-2 border-0">Date</th>
                      <th className="px-3 py-2 border-0">Supplier</th>
                      <th className="px-3 py-2 border-0 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockHistory.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4 text-muted">
                          <FaBox className="mb-2" size={24} />
                          <br />
                          No stock entries found
                        </td>
                      </tr>
                    ) : (
                      stockHistory.slice(0, 20).map((stock) => (
                        <tr key={stock.id}>
                          <td className="px-3 py-2">
                            <div className="fw-medium">{stock.item_name || 'N/A'}</div>
                            <small className="text-muted">{stock.item_code}</small>
                          </td>
                          <td className="px-3 py-2">
                            <span className="fw-medium">{stock.stock_in}</span>
                            <small className="text-muted d-block">{stock.unit}</small>
                          </td>
                          <td className="px-3 py-2">
                            ฿{parseFloat(stock.purchase_price || 0).toFixed(2)}
                          </td>
                          <td className="px-3 py-2">
                            <span className="fw-medium text-success">
                              ฿{parseFloat(stock.netAmount || 0).toFixed(2)}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {new Date(stock.pdate).toLocaleDateString()}
                          </td>
                          <td className="px-3 py-2">
                            {stock.supplier_name || 'N/A'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="d-flex gap-1 justify-content-center">
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => handleEditStock(stock)}
                                title="Edit"
                                style={{ borderRadius: '6px' }}
                              >
                                <FaEdit size={12} />
                              </button>
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleDeleteStock(stock.id)}
                                title="Delete"
                                style={{ borderRadius: '6px' }}
                              >
                                <FaTrash size={12} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Inventory Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card border-0 shadow-sm" style={{ 
            borderRadius: '12px',
            border: isEditMode ? '2px solid #10b981' : '2px solid #3b82f6',
            backgroundColor: isEditMode ? '#f0fdf4' : '#eff6ff'
          }}>
            <div className="card-header border-0 pb-2" style={{ 
              background: isEditMode ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              color: 'white'
            }}>
              <h6 className="mb-0 fw-bold d-flex align-items-center">
                <FaEdit className="me-2" size={18} />
                {isEditMode ? `Editing Inventory - Ref: ${editRefNo}` : 'Load Inventory for Edit'}
              </h6>
            </div>
            <div className="card-body">
              {!isEditMode ? (
                <>
                  <div className="row align-items-end">
                    <div className="col-md-8">
                      <label className="form-label small fw-semibold">Reference Number</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type="text"
                          className="form-control"
                          value={searchRefNo}
                          onChange={(e) => handleRefNoChange(e.target.value)}
                          onFocus={() => {
                            if (searchRefNo.trim() && refNoSuggestions.filter(r => r.toLowerCase().includes(searchRefNo.toLowerCase())).length > 0) {
                              setShowSuggestions(true);
                            }
                          }}
                          onBlur={() => {
                            // Delay to allow click on suggestion
                            setTimeout(() => setShowSuggestions(false), 200);
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              setShowSuggestions(false);
                              loadInventoryForEdit();
                            }
                          }}
                          placeholder="Type or select Reference Number..."
                          style={{ borderRadius: '8px', borderColor: '#3b82f6' }}
                          autoComplete="off"
                        />
                        
                        {/* Autocomplete Suggestions Dropdown */}
                        {showSuggestions && (
                          <div 
                            className="position-absolute w-100 bg-white border rounded shadow-lg"
                            style={{ 
                              top: '100%', 
                              left: 0, 
                              zIndex: 1000,
                              maxHeight: '250px',
                              overflowY: 'auto',
                              marginTop: '4px'
                            }}
                          >
                            <div className="list-group list-group-flush">
                              {refNoSuggestions
                                .filter(refNo => refNo.toLowerCase().includes(searchRefNo.toLowerCase()))
                                .slice(0, 10)
                                .map((refNo, index) => (
                                  <button
                                    key={index}
                                    type="button"
                                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                    onClick={() => selectRefNoSuggestion(refNo)}
                                    style={{ 
                                      cursor: 'pointer',
                                      padding: '10px 15px',
                                      border: 'none',
                                      borderBottom: '1px solid #e5e7eb'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.target.style.backgroundColor = '#f0f4ff';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.backgroundColor = 'transparent';
                                    }}
                                  >
                                    <span className="fw-semibold text-primary">
                                      <i className="fas fa-file-invoice me-2"></i>
                                      {refNo}
                                    </span>
                                    <small className="text-muted">
                                      Click to select
                                    </small>
                                  </button>
                                ))}
                              
                              {refNoSuggestions.filter(refNo => refNo.toLowerCase().includes(searchRefNo.toLowerCase())).length === 0 && (
                                <div className="list-group-item text-center text-muted py-3">
                                  <i className="fas fa-search me-2"></i>
                                  No matching reference numbers found
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <button
                        className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                        onClick={loadInventoryForEdit}
                        disabled={loading}
                        style={{ borderRadius: '8px', fontWeight: '500', height: '38px' }}
                      >
                        {loading ? (
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                        ) : (
                          <FaEdit className="me-2" size={14} />
                        )}
                        Load for Edit
                      </button>
                    </div>
                  </div>
                  <small className="text-muted d-block mt-2">
                    Enter the reference number of the inventory you want to edit
                  </small>
                </>
              ) : (
                <div className="alert alert-success mb-0" style={{ 
                  backgroundColor: '#d1fae5', 
                  borderColor: '#10b981',
                  color: '#047857'
                }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>📝 Edit Mode Active</strong>
                      <p className="mb-0 mt-1 small">Make changes to items below and click "Save Changes" to update inventory</p>
                    </div>
                    <button
                      className="btn btn-sm text-white"
                      onClick={cancelEditMode}
                      style={{ 
                        borderRadius: '6px',
                        background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                        border: 'none',
                        fontWeight: '500'
                      }}
                    >
                      Cancel Edit
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons - Moved to Bottom */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card border-0 shadow-lg" style={{ 
            borderRadius: '12px',
            position: 'sticky',
            bottom: '20px',
            zIndex: 100,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
          }}>
            <div className="card-body p-3">
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {!isEditMode && (
                  <>
                    <button 
                      className="btn d-flex align-items-center text-white"
                      onClick={addNewStockEntry}
                      style={{ 
                        borderRadius: '8px', 
                        fontWeight: '500',
                        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                        border: 'none'
                      }}
                    >
                      <FaPlus className="me-2" size={14} />
                      Add Entry (F1)
                    </button>
                    
                    <button 
                      className="btn btn-success d-flex align-items-center"
                      onClick={saveBillToDatabase}
                      disabled={loading || !supplier || !billRefNo.trim() || stockEntries.filter(e => e.item_id && e.stock_in).length === 0}
                      style={{ borderRadius: '8px', fontWeight: '500', fontSize: '1.1rem', padding: '10px 24px' }}
                    >
                      {loading ? (
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                      ) : (
                        <FaSave className="me-2" size={16} />
                      )}
                      Save Bill (F2)
                    </button>
                  </>
                )}

                {isEditMode && (
                  <button 
                    className="btn btn-success d-flex align-items-center"
                    onClick={saveEditedInventory}
                    disabled={loading || !supplier || stockEntries.filter(e => e.item_id && e.stock_in).length === 0}
                    style={{ borderRadius: '8px', fontWeight: '500', fontSize: '1.1rem', padding: '10px 24px' }}
                  >
                    {loading ? (
                      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                    ) : (
                      <FaSave className="me-2" size={16} />
                    )}
                    Save Changes
                  </button>
                )}
                
                <button 
                  className="btn d-flex align-items-center text-white"
                  onClick={isEditMode ? cancelEditMode : clearForm}
                  style={{ 
                    borderRadius: '8px', 
                    fontWeight: '500',
                    background: isEditMode ? 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)' : 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)',
                    border: 'none'
                  }}
                >
                  <FaBroom className="me-2" size={14} />
                  {isEditMode ? 'Cancel Edit' : 'Clear (F3)'}
                </button>
                
                <button 
                  className="btn d-flex align-items-center text-white"
                  onClick={loadStockHistory}
                  style={{ 
                    borderRadius: '8px', 
                    fontWeight: '500',
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    border: 'none'
                  }}
                >
                  <FaEye className="me-2" size={14} />
                  Refresh (F4)
                </button>
                
                {!isEditMode && (
                  <div className="ms-auto d-flex align-items-center gap-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="autoVat"
                        checked={autoVat}
                        onChange={(e) => setAutoVat(e.target.checked)}
                      />
                      <label className="form-check-label small text-white fw-semibold" htmlFor="autoVat">
                        Auto VAT ({vatPercentage}%)
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Main render with proper Layout structure
  return (
    <Layout>
      <Header title="📦 Stock Management System" />
      <div className="container-fluid p-4">
        {renderContent()}
      </div>
    </Layout>
  );
}